
import { supabase } from './supabase';

const AVATARS_BUCKET = 'avatars';
const DESTINATIONS_BUCKET = 'destinations';
const PLACES_BUCKET = 'places';
const VERIFICATION_BUCKET = 'verification';
const BLOG_IMAGES_BUCKET = 'blog-images';

// Initialize storage by creating the necessary buckets if they don't exist
export async function initializeStorage(): Promise<void> {
  try {
    // Check if buckets exist before trying to create them
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error('Error fetching buckets:', error);
      return;
    }
    
    const existingBuckets = new Set(buckets.map(bucket => bucket.name));
    
    // Create buckets only if they don't exist
    await createBucketIfNotExists(AVATARS_BUCKET, existingBuckets);
    await createBucketIfNotExists(DESTINATIONS_BUCKET, existingBuckets);
    await createBucketIfNotExists(PLACES_BUCKET, existingBuckets);
    await createBucketIfNotExists(VERIFICATION_BUCKET, existingBuckets);
    await createBucketIfNotExists(BLOG_IMAGES_BUCKET, existingBuckets);
  } catch (err) {
    // Don't throw errors on initialization - just log them
    // This prevents the app from crashing if storage can't be initialized
    console.error('Storage initialization error:', err);
  }
}

async function createBucketIfNotExists(bucketName: string, existingBuckets: Set<string>): Promise<void> {
  try {
    // Skip creation if bucket already exists
    if (existingBuckets.has(bucketName)) {
      return;
    }
    
    // Check if user has permission before attempting to create
    const { data: userSession } = await supabase.auth.getSession();
    if (!userSession.session) {
      console.log(`User not logged in, skipping ${bucketName} bucket creation`);
      return;
    }
    
    const { data, error } = await supabase.storage.createBucket(bucketName, {
      public: true,
      fileSizeLimit: 10 * 1024 * 1024, // 10 MB
    });
    
    if (error) {
      // Check if this is an RLS policy violation
      if (error.message.includes('new row violates row-level security policy')) {
        console.warn(`RLS policy prevented creation of ${bucketName} bucket. This is normal for non-admin users.`);
      } else {
        console.warn(`Error creating ${bucketName} bucket:`, error);
      }
      return;
    }
    
    console.log(`Created ${bucketName} bucket:`, data);
  } catch (err) {
    console.warn(`Error creating ${bucketName} bucket:`, err);
  }
}

// Add the ensureAvatarsBucketExists function that's referenced in AvatarUpload.tsx
export async function ensureAvatarsBucketExists(): Promise<void> {
  try {
    // Check if user is logged in first
    const { data: userSession } = await supabase.auth.getSession();
    if (!userSession.session) {
      console.log('User not logged in, skipping avatars bucket check');
      return;
    }
    
    // Check if avatars bucket exists
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error('Error fetching buckets:', error);
      // Don't throw error, just log it
      return;
    }
    
    // Check if avatars bucket already exists
    const avatarsBucketExists = buckets.some(bucket => bucket.name === AVATARS_BUCKET);
    
    if (!avatarsBucketExists) {
      console.log('Avatars bucket does not exist, creating it...');
      const { error: createError } = await supabase.storage.createBucket(AVATARS_BUCKET, {
        public: true,
        fileSizeLimit: 10 * 1024 * 1024, // 10 MB
      });
      
      if (createError) {
        // Check if this is an RLS policy violation
        if (createError.message.includes('new row violates row-level security policy')) {
          console.warn("RLS policy prevented creation of avatars bucket. This is normal for non-admin users.");
        } else {
          console.error('Error creating avatars bucket:', createError);
        }
        // Don't throw error, just log it
        return;
      }
      
      console.log('Avatars bucket created successfully');
    } else {
      console.log('Avatars bucket already exists');
    }
  } catch (err) {
    console.error('Error ensuring avatars bucket exists:', err);
    // Don't throw the error, just log it to prevent breaking the app flow
    // This way the UI will still work even if the bucket can't be created
  }
}

export async function uploadImage(
  bucket: string, 
  filePath: string, 
  file: File
): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        upsert: true,
        contentType: file.type,
      });

    if (error) {
      console.error(`Error uploading to ${bucket}:`, error);
      return null;
    }

    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path);

    return publicUrlData.publicUrl;
  } catch (err) {
    console.error(`Error in uploadImage to ${bucket}:`, err);
    return null;
  }
}

export async function deleteImage(bucket: string, filePath: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (error) {
      console.error(`Error deleting from ${bucket}:`, error);
      return false;
    }

    return true;
  } catch (err) {
    console.error(`Error in deleteImage from ${bucket}:`, err);
    return false;
  }
}
